import {
  StandardTile,
  StandardTileContents,
  StandardTileDescription,
  StandardTileImage,
  StandardTileName,
  StandardTileOverlay,
  StandardTilePrice,
  StandardTileSeparatelyClickableContents,
  useScreenClass,
  Typography,
  FlexBox,
  BasicResponsiveImage,
  RatingsStars
} from '@vp/swan'
import { useStyles } from '@vp/ubik-context'
import { CouponCallout } from '@vp/coupon-callout'
import { ProductFavoriteHeart, useFavoriteHeartContext } from '@vp/favorites-heart'
import { PriceDisplay } from '@vp/price-platform'

import React from 'react'
import classNames from 'classnames'
import { stylesheet } from '../styles/ProductTile.scss'
import { trackClickEvent } from '../utils/tracking/tracking'
import { useAppContext } from '../state/AppContext'
import { ReviewData } from '../types/Contentful/ContentfulTypes'
import { ProductTileColorSwatches } from './ProductTileColorSwatches'
import { LinkWrapper } from './LinkWrapper'

import { MSXAttibuteValue } from '../types/Msx'
import BulletPointsDescription from './BulletPointsDescription'
import { showProductHeart } from '../utils/ProductTile/productTile'
import { CalloutWrapper } from './CalloutWrapper'
import { ProductCallout, ProductImage } from './AutoGenerate/types/AutomatedExperience'

export interface ProductTileV2Props {
  name: string
  description?: string
  formattedDescription?: string
  image?: ProductImage
  linkUrl?: string
  callout?: ProductCallout | ProductCallout[]
  review?: ReviewData
  layout: 'vertical' | 'horizontal'
  disabled: boolean
  index: number
  pageZone: string
  mpvId?: string
  productId?: string
  imageRef?: React.RefObject<HTMLImageElement>
  showPrice: boolean
  enableCampaignCallouts: boolean
  colorInformation?: MSXAttibuteValue[]
  colorSwatchCount: number
  version?: number
  selections: Record<string, string>
  quantity?: number
}

export const ProductTileV2: React.FC<ProductTileV2Props> = (props: ProductTileV2Props) => {
  const { merchandisingCategoryId } = useAppContext()
  const {
    name,
    description,
    formattedDescription,
    linkUrl,
    image,
    callout,
    layout,
    disabled,
    index,
    pageZone,
    mpvId,
    productId,
    imageRef,
    showPrice,
    enableCampaignCallouts,
    review,
    colorInformation,
    colorSwatchCount,
    version,
    selections,
    quantity
  } = props
  useStyles(stylesheet)
  const lines = description?.split('\n')
  const isMobile = useScreenClass() === 'xs'
  const { isFavoriteDataReady } = useFavoriteHeartContext()

  return (
    <StandardTile
      className={classNames({
        'tile-disabled': disabled
      })}
      layout={layout}
      mb={isMobile && layout !== 'vertical' ? 0 : '5'}
      imageWidth="standard"
    >
      <StandardTileOverlay>
        <LinkWrapper
          href={linkUrl}
          skin="unstyled"
          data-section={pageZone}
          data-position={index}
          data-translation={mpvId}
          onClick={() => {
            trackClickEvent({
              categoryId: merchandisingCategoryId,
              pageZone,
              ctaValue: mpvId || name,
              destinationUrl: linkUrl!,
              index
            })
          }}
        >
          {callout && (
            <CalloutWrapper
              callout={callout}
              enableCampaignCallouts={enableCampaignCallouts}
              mpvId={mpvId!}
            />
          )}
        </LinkWrapper>
        {showProductHeart(productId, version, name, mpvId, isFavoriteDataReady) && (
          <StandardTileSeparatelyClickableContents onClick={(event) => event.stopPropagation()}>
            <ProductFavoriteHeart
              productKey={productId!}
              productVersion={version!}
              selectedOptions={{}}
              productName={name!}
              mpvId={mpvId!}
              pageZone={pageZone}
              productPosition={index}
            />
          </StandardTileSeparatelyClickableContents>
        )}
      </StandardTileOverlay>

      <StandardTileImage
        className={classNames({
          'tile-disabled-image': disabled
        })}
      >
        <LinkWrapper
          href={linkUrl}
          skin="unstyled"
          data-section={pageZone}
          data-position={index}
          data-translation={mpvId}
          onClick={() => {
            trackClickEvent({
              categoryId: merchandisingCategoryId,
              pageZone,
              ctaValue: mpvId || name,
              destinationUrl: linkUrl!,
              index
            })
          }}
        >
          <BasicResponsiveImage
            aspectRatio={1}
            src={image?.src}
            ref={imageRef}
            alt={image?.altTextOverride || ''}
          />
        </LinkWrapper>
      </StandardTileImage>

      <StandardTileContents
        className={classNames({
          'tile-disabled-contents': disabled
        })}
      >
        <LinkWrapper
          href={linkUrl}
          skin="unstyled"
          data-section={pageZone}
          data-position={index}
          data-translation={mpvId}
          onClick={() => {
            trackClickEvent({
              categoryId: merchandisingCategoryId,
              pageZone,
              ctaValue: mpvId || name,
              destinationUrl: linkUrl!,
              index
            })
          }}
        >
          {colorInformation && colorInformation.length > 1 && (
            <ProductTileColorSwatches
              colorInformation={colorInformation}
              colorSwatchCount={colorSwatchCount}
            />
          )}
          <StandardTileName>{name}</StandardTileName>
          {formattedDescription ? (
            <StandardTileDescription
              mt={3}
              mb={3}
              dangerouslySetInnerHTML={{ __html: formattedDescription }}
            />
          ) : (
            <StandardTileDescription mt={3} mb={3}>
              <BulletPointsDescription text={lines} />
            </StandardTileDescription>
          )}
          {review && (
            <FlexBox alignItems="center" flexWrap="wrap" flexDirection="row">
              <RatingsStars
                ratingValue={review.averageRating}
                aria-label={review.reviewedByNCustomersLabel}
              />
              <Typography textColor="subtle" paddingLeft="2">
                ({review.reviewsCount})
              </Typography>
            </FlexBox>
          )}
          {showPrice && mpvId && (
            <StandardTilePrice>
              <PriceDisplay
                mpvId={mpvId}
                {...(quantity !== undefined && { quantity })}
                {...(selections && Object.keys(selections).length > 0 && { selections })}
              />
            </StandardTilePrice>
          )}
        </LinkWrapper>
        {mpvId && (
          <StandardTileSeparatelyClickableContents>
            <CouponCallout
              mpvId={mpvId}
              {...(selections && Object.keys(selections).length > 0 && { selections })}
              {...(quantity !== undefined && { quantity })}
              trackingData={{ pageZone }}
            />
          </StandardTileSeparatelyClickableContents>
        )}
      </StandardTileContents>
    </StandardTile>
  )
}
